'use client';

import Image from 'next/image';
import { useRef } from 'react';
import { useLoginDialog } from '@/components/login-dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/auth-context';
import { useLanguage } from '@/contexts/language-context';
import { normalizeUserLevel } from '@/lib/feature-permissions';
import { getPricingUrl, getSignUpUrl } from '@/lib/pricing-url';

type UserLevel = ReturnType<typeof normalizeUserLevel>;
type Translate = ReturnType<typeof useLanguage>['t'];

interface EntitlementAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level?: unknown;
}

interface Benefit {
  title: string;
  description: string;
}

interface TierCopy {
  title: string;
  subtitle: string;
  benefits: Benefit[];
  primary: string;
}

function getTierCopy(level: UserLevel, t: Translate): TierCopy {
  if (level === 'L0') {
    return {
      title: t('entitlementAccess.l0Title'),
      subtitle: t('entitlementAccess.l0Subtitle'),
      benefits: [
        {
          title: t('entitlementAccess.l0BenefitSessionsTitle'),
          description: t('entitlementAccess.l0BenefitSessionsDescription'),
        },
        {
          title: t('entitlementAccess.l0BenefitChartTitle'),
          description: t('entitlementAccess.l0BenefitChartDescription'),
        },
        {
          title: t('entitlementAccess.l0BenefitProfileTitle'),
          description: t('entitlementAccess.l0BenefitProfileDescription'),
        },
      ],
      primary: t('entitlementAccess.createAccount'),
    };
  }

  if (level === 'L1') {
    return {
      title: t('entitlementAccess.l1Title'),
      subtitle: t('entitlementAccess.l1Subtitle'),
      benefits: [
        {
          title: t('entitlementAccess.l1BenefitSessionsTitle'),
          description: t('entitlementAccess.l1BenefitSessionsDescription'),
        },
        {
          title: t('entitlementAccess.l1BenefitSummaryTitle'),
          description: t('entitlementAccess.l1BenefitSummaryDescription'),
        },
        {
          title: t('entitlementAccess.l1BenefitReviewTitle'),
          description: t('entitlementAccess.l1BenefitReviewDescription'),
        },
      ],
      primary: t('entitlementAccess.purchaseChallenge'),
    };
  }

  return {
    title: t('entitlementAccess.l23Title'),
    subtitle: t('entitlementAccess.l23Subtitle'),
    benefits: [
      {
        title: t('entitlementAccess.l23BenefitSessionsTitle'),
        description: t('entitlementAccess.l23BenefitSessionsDescription'),
      },
      {
        title: t('entitlementAccess.l23BenefitFeaturesTitle'),
        description: t('entitlementAccess.l23BenefitFeaturesDescription'),
      },
    ],
    primary: t('entitlementAccess.purchaseAgain'),
  };
}

export function EntitlementAccessDialog({ open, onOpenChange, level }: EntitlementAccessDialogProps) {
  const primaryActionRef = useRef<HTMLButtonElement>(null);
  const { user } = useAuth();
  const { isRTL, t } = useLanguage();
  const { openLoginDialog } = useLoginDialog();
  const normalizedLevel = normalizeUserLevel(level ?? user?.level);
  const copy = getTierCopy(normalizedLevel, t);
  const isGuest = normalizedLevel === 'L0';
  const contentWidth = normalizedLevel === 'L0' ? 438 : normalizedLevel === 'L1' ? 435 : 444;

  const handlePrimaryAction = () => {
    const targetUrl = isGuest ? getSignUpUrl() : getPricingUrl(user?.pricing_url);
    if (targetUrl && typeof window !== 'undefined') {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleSignIn = () => {
    onOpenChange(false);
    openLoginDialog();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}>
      <AlertDialogContent
        size="wide"
        data-entitlement-level={normalizedLevel}
        dir={isRTL ? 'rtl' : 'ltr'}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          primaryActionRef.current?.focus();
        }}
        overlayClassName="bg-black/75 backdrop-blur-[2px]"
        className="isolate block overflow-hidden rounded-[20px] border-0 bg-[#070400] p-0 text-white shadow-none sm:max-w-[540px]">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-[-155.64px] top-[-98.87px] h-[184.73px] w-[326.32px] rounded-full bg-[#F9A215] opacity-50"
          style={{
            boxShadow: '0 0 40px 35px rgba(249, 135, 21, 0.8), 0 0 170px 150px rgba(234, 88, 12, 0.7)',
            filter: 'blur(100px)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-[207px] top-[440px] h-[184.73px] w-[326.32px] rounded-full bg-[#F9A215] opacity-10"
          style={{
            boxShadow: '0 0 40px 35px rgba(249, 135, 21, 0.8), 0 0 170px 150px rgba(234, 88, 12, 0.7)',
            filter: 'blur(100px)',
          }}
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-20 rounded-[20px] p-px"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 193, 130, 0.5) 0%, rgba(255, 193, 130, 0.1) 57%)',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        />

        <div className="relative z-10 flex flex-col items-center gap-6 p-12">
          <AlertDialogCancel
            size="icon"
            variant="ghost"
            aria-label={t('settings.close')}
            className="absolute right-2 top-2 rounded-full border-0 bg-transparent p-0 text-white shadow-none hover:bg-white/10 hover:text-white">
            <Image
              src="/images/entitlement-dialog/close.svg"
              alt=""
              width={20}
              height={20}
              aria-hidden="true"
            />
          </AlertDialogCancel>

          <Image
            src="/images/entitlement-dialog/aifo-logo.svg"
            alt="AIFO"
            width={88}
            height={22}
            className="h-[22px] w-[88px]"
          />

          <div
            data-entitlement-content=""
            className="flex max-w-full flex-col gap-12"
            style={{ width: contentWidth }}>
            <div className="flex flex-col items-center gap-1.5">
              <AlertDialogTitle className="text-center text-[26px] font-semibold leading-[34px] tracking-[-0.01em] text-white sm:whitespace-nowrap">{copy.title}</AlertDialogTitle>
              <AlertDialogDescription className="text-left text-base font-normal leading-5 text-[#666666] sm:whitespace-nowrap">{copy.subtitle}</AlertDialogDescription>
            </div>

            <div className="flex w-full flex-col gap-12">
              <div className="flex flex-col gap-9">
                {copy.benefits.map((benefit) => (
                  <div
                    key={benefit.title}
                    className="flex items-center gap-3">
                    <Image
                      src="/images/entitlement-dialog/benefit-check.svg"
                      alt=""
                      width={42}
                      height={42}
                      className="size-[42px] shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex min-w-0 flex-col justify-center gap-1 text-start">
                      <div className="text-base font-semibold leading-5 text-white">{benefit.title}</div>
                      <div className="text-sm font-normal leading-4 tracking-[-0.01em] text-[#666666]">{benefit.description}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex w-full flex-col items-center gap-2">
                <div className="flex w-full flex-col gap-4">
                  <AlertDialogAction
                    ref={primaryActionRef}
                    variant="ghost"
                    onClick={handlePrimaryAction}
                    className="h-auto min-h-0 w-full rounded-lg px-10 py-[13px] !text-base font-medium leading-5 tracking-[-0.01em] text-white hover:text-white"
                    style={{ backgroundColor: '#F98715' }}>
                    {copy.primary}
                  </AlertDialogAction>
                  <AlertDialogCancel
                    variant="outline"
                    className="h-auto min-h-0 w-full rounded-lg border border-white/[0.16] px-10 py-[13px] !text-base font-medium leading-5 tracking-[-0.01em] text-white shadow-none hover:text-white"
                    style={{ backgroundColor: 'transparent' }}>
                    {t('entitlementAccess.notNow')}
                  </AlertDialogCancel>
                </div>

                <div className="flex min-h-4 items-center justify-center gap-2 text-center text-sm font-normal leading-4 tracking-[-0.01em] text-[#666666]">
                  {isGuest ? (
                    <>
                      <span>{t('entitlementAccess.alreadyRegistered')}</span>
                      <button
                        type="button"
                        onClick={handleSignIn}
                        className="text-[#F98715] hover:underline">
                        {t('entitlementAccess.signIn')}
                      </button>
                    </>
                  ) : (
                    <span>{t('entitlementAccess.benefitsEffective')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
